/** The fallback read-out for an action's `detail()`.
 *
 *  `detail` is `Record<string, unknown>` and reached the UI only through
 *  `ActionCustomUI`'s switch, so an action with rich read-state and no bespoke
 *  component showed **nothing at all** — the state of `CATALOG_SYNC`,
 *  `BOOKING_BACKFILL`, `AUX_INFO_SYNC` and `NOTIFICATION_PACK` on the Preflight
 *  tab, whose UI exists only inside the wizard.
 *
 *  Deliberately modest: scalars as a key/value grid, lists of objects as a
 *  compact table, and anything deeper behind a disclosure with a copy button.
 *  A bespoke control registered in the switch always wins — this is what the
 *  operator gets instead of a blank space, not a replacement for one.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import type { PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { formatJson, formatScalar, formatValue, humanizeKey, isScalar } from '@/features/preflight/detailFormat';

import { copyOnlyActions, urlActions } from '../tenantLinks';

/** Rows of a list-of-objects, capped — the full value is in the raw disclosure. */
const TABLE_ROW_CAP = 8;

/** Shortest opaque string worth a copy action. Below this a value is a word
 *  ("yes", "ok", a count) and a copy chip is noise. */
const COPYABLE_MIN_LENGTH = 12;

/** What a generic read-state scalar can offer. `detail()` is free-form, so this
 *  reads the VALUE rather than the key: a URL opens, an opaque identifier (a
 *  SID, an ARN, a bucket key) copies, and everything else stays plain text. */
function scalarActions(value: unknown, key: string): ValueAction[] {
  if (typeof value !== 'string') return [];
  const v = value.trim();
  if (/^https?:\/\/\S+$/i.test(v)) return urlActions(v, `Open ${humanizeKey(key)}`);
  if (v.length >= COPYABLE_MIN_LENGTH && !/\s/.test(v)) {
    return copyOnlyActions(v, `Copy ${humanizeKey(key)}`);
  }
  return [];
}

function isObjectList(v: unknown): v is Record<string, unknown>[] {
  return (
    Array.isArray(v) &&
    v.length > 0 &&
    v.every((e) => e != null && typeof e === 'object' && !Array.isArray(e))
  );
}

function ObjectTable({ rows }: { rows: Record<string, unknown>[] }) {
  const columns = [...new Set(rows.flatMap((r) => Object.keys(r)))].slice(0, 6);
  const shown = rows.slice(0, TABLE_ROW_CAP);
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ '& td, & th': { py: 0.25, px: 1, fontSize: 12 } }}>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c} sx={{ color: 'text.secondary' }}>
                {humanizeKey(c)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((r, i) => (
            <TableRow key={i}>
              {columns.map((c) => (
                <TableCell key={c} sx={{ wordBreak: 'break-word' }}>
                  <ValueActions actions={scalarActions(r[c], c)}>{formatValue(r[c])}</ValueActions>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > shown.length && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
          +{rows.length - shown.length} more — see the raw value below.
        </Typography>
      )}
    </Box>
  );
}

export function PreflightDetailDisplay({ item }: { item: PreflightItem }) {
  const snackbar = useSnackbar();
  const detail = item.detail ?? {};
  const keys = Object.keys(detail);
  if (keys.length === 0) return null;

  const scalars = keys.filter((k) => isScalar(detail[k]));
  const tables = keys.filter((k) => isObjectList(detail[k]));
  const rest = keys.filter((k) => !scalars.includes(k) && !tables.includes(k));

  const copy = () => {
    void navigator.clipboard
      ?.writeText(formatJson(detail))
      .then(() => snackbar.show('Copied.', 'success'))
      .catch(() => snackbar.show('Could not copy.', 'error'));
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      {scalars.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 1.5,
            rowGap: 0.25,
            mb: tables.length || rest.length ? 1 : 0,
          }}
        >
          {scalars.map((k) => (
            <Box key={k} sx={{ display: 'contents' }}>
              <Typography variant="caption" color="text.secondary">
                {humanizeKey(k)}
              </Typography>
              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                <ValueActions actions={scalarActions(detail[k], k)}>
                  {formatScalar(detail[k])}
                </ValueActions>
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {tables.map((k) => (
        <Box key={k} sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {humanizeKey(k)}
          </Typography>
          <ObjectTable rows={detail[k] as Record<string, unknown>[]} />
        </Box>
      ))}

      {(rest.length > 0 || tables.length > 0) && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 0, px: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Raw read-state
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Box sx={{ position: 'relative' }}>
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={copy}
                  aria-label="Copy read-state"
                  sx={{ position: 'absolute', top: 4, right: 4 }}
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  pr: 4,
                  maxHeight: 280,
                  overflow: 'auto',
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {formatJson(detail)}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}
